import React, { useEffect, useRef } from "react";
import { canAnimate, clamp, ease } from "./motion";
import { BOX, humanoidSprites } from "./humanoid";
import { COMMAND_APPS, drawAgentPod, drawCommandCenter } from "./workstation";

const INK = "#191919";

function roundRect(g, x, y, w, h, r) {
  g.beginPath();
  if (g.roundRect) g.roundRect(x, y, w, h, r);
  else g.rect(x, y, w, h);
}

function pill(g, x, y, text, dark, maxW) {
  g.font = "600 11px Manrope, Arial, sans-serif";
  const pw = g.measureText(text).width + 20;
  const px = clamp(x - pw / 2, 4, maxW - pw - 4);
  roundRect(g, px, y - 20, pw, 20, 10);
  g.fillStyle = dark ? INK : "#fff";
  g.fill();
  g.strokeStyle = "rgba(25,25,25,0.18)";
  g.lineWidth = 1;
  g.stroke();
  g.fillStyle = dark ? "#fff" : INK;
  g.textBaseline = "middle";
  g.fillText(text, px + 10, y - 9.5);
}

const layers = new WeakMap();
function cloudLayer(canvas) {
  let layer = layers.get(canvas);
  if (!layer || layer.width !== canvas.width || layer.height !== canvas.height) {
    layer = document.createElement("canvas");
    layer.width = canvas.width;
    layer.height = canvas.height;
    layers.set(canvas, layer);
  }
  return layer;
}

// 01: one worker with access to everything: monitors on two tiers, a laptop, a tablet and a phone,
// moving its attention from app to app.
function personal(g, w, h, t) {
  // It runs on your own devices, or remotely in the cloud: the scene alternates between the two.
  const cycle = 8000;
  const m = (t % cycle) / cycle;
  const cloud = ease(clamp((m - 0.45) / 0.08)) * (1 - ease(clamp((m - 0.93) / 0.07)));
  const base = Math.min(w / 3.75, (h - 34) / 2.62);
  const u = base * (1 - 0.22 * cloud);
  const ox = w / 2 - 0.12 * u;
  const oy = 2.06 * u + 30 - cloud * 12;
  const ph = (t / 1600) % 1;
  g.save();
  g.translate(ox, oy);
  const screens = drawCommandCenter(g, u, ph);
  g.restore();
  if (cloud > 0.01) {
    g.globalAlpha = cloud;
    // The station sits in the cloud: puffs stroked, then filled, leaving one outline over its base
    const puffs = [
      [-1.62, 0.44, 0.36],
      [-1.12, 0.26, 0.48],
      [-0.5, 0.3, 0.54],
      [0.18, 0.34, 0.52],
      [0.84, 0.2, 0.5],
      [1.46, 0.2, 0.44],
      [1.95, 0.34, 0.32],
      [-1.3, 0.66, 0.4],
      [-0.6, 0.76, 0.48],
      [0.2, 0.8, 0.5],
      [0.95, 0.7, 0.46],
      [1.6, 0.56, 0.36],
    ].map(([dx, dy, r]) => [ox + dx * u, oy + dy * u, r * u]);
    // Drawn whole on its own layer, then faded, so the puffs never show through each other
    const layer = cloudLayer(g.canvas);
    const c = layer.getContext("2d");
    c.setTransform(g.getTransform());
    c.clearRect(0, 0, w, h);
    c.beginPath();
    puffs.forEach(([x, y, r]) => {
      c.moveTo(x + r, y);
      c.arc(x, y, r, 0, Math.PI * 2);
    });
    c.strokeStyle = "rgba(25,25,25,0.5)";
    c.lineWidth = 2.2;
    c.stroke();
    c.fillStyle = "#ffffff";
    c.fill();
    g.save();
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.drawImage(layer, 0, 0);
    g.restore();
    // Your devices below, receiving the work streamed from the cloud
    const bottom = Math.min(h - 4, oy + 1.55 * u + 30);
    const lx = w * 0.14;
    const px = w * 0.86;
    const from = [
      [ox - 0.9 * u, oy + 1.2 * u],
      [ox + 1.1 * u, oy + 1.12 * u],
    ];
    g.setLineDash([2, 4]);
    g.beginPath();
    g.moveTo(from[0][0], from[0][1]);
    g.lineTo(lx, bottom - 19);
    g.moveTo(from[1][0], from[1][1]);
    g.lineTo(px, bottom - 23);
    g.strokeStyle = "rgba(25,25,25,0.4)";
    g.lineWidth = 1;
    g.stroke();
    g.setLineDash([]);
    [
      [from[0], [lx, bottom - 19], 0],
      [from[1], [px, bottom - 23], 0.5],
    ].forEach(([[x0, y0], [x1, y1], off]) => {
      const q = (((t / 1400 + off) % 1) + 1) % 1;
      g.beginPath();
      g.arc(x0 + (x1 - x0) * q, y0 + (y1 - y0) * q, 2.2, 0, Math.PI * 2);
      g.fillStyle = INK;
      g.fill();
    });
    roundRect(g, lx - 15, bottom - 19, 30, 18, 2);
    g.fillStyle = INK;
    g.fill();
    g.fillStyle = "#fff";
    g.fillRect(lx - 12.5, bottom - 16.5, 25, 13);
    g.fillStyle = "#e0e0e0";
    g.fillRect(lx - 19, bottom - 1, 38, 3);
    roundRect(g, px - 6.5, bottom - 23, 13, 23, 2.5);
    g.fillStyle = INK;
    g.fill();
    g.fillStyle = "#fff";
    g.fillRect(px - 4.5, bottom - 20.5, 9, 16);
    g.globalAlpha = 1;
  }
  g.font = "600 11px Manrope, Arial, sans-serif";
  pill(g, 60, 22, cloud > 0.5 ? "In the cloud" : "On your devices", false, w);
  const dwell = 1500;
  const focus = Math.floor(t / dwell) % screens.length;
  const q = (t % dwell) / dwell;
  const quad = screens[focus].map(([x, y]) => [x + ox, y + oy]);
  const [tl, tr, br, bl] = quad;
  // The worker's attention: a line from its head to the app it is using
  const head = [ox + 0.22 * u, oy + (0.11 - 1.04) * u];
  const mid = [(tl[0] + br[0]) / 2, (tl[1] + br[1]) / 2];
  g.setLineDash([2, 4]);
  g.beginPath();
  g.moveTo(head[0], head[1]);
  g.lineTo(head[0] + (mid[0] - head[0]) * ease(clamp(q * 4)), head[1] + (mid[1] - head[1]) * ease(clamp(q * 4)));
  g.strokeStyle = "rgba(25,25,25,0.5)";
  g.lineWidth = 1;
  g.stroke();
  g.setLineDash([]);
  g.beginPath();
  quad.forEach(([x, y], k) => (k ? g.lineTo(x, y) : g.moveTo(x, y)));
  g.closePath();
  g.strokeStyle = "rgba(25,25,25,0.16)";
  g.lineWidth = 6;
  g.stroke();
  g.strokeStyle = INK;
  g.lineWidth = 1.6;
  g.stroke();
  // The pointer, working inside the app
  const cxp = tl[0] + (tr[0] - tl[0]) * (0.3 + 0.4 * Math.sin(q * Math.PI)) + (bl[0] - tl[0]) * (0.35 + 0.3 * q);
  const cyp = tl[1] + (tr[1] - tl[1]) * (0.3 + 0.4 * Math.sin(q * Math.PI)) + (bl[1] - tl[1]) * (0.35 + 0.3 * q);
  g.beginPath();
  g.moveTo(cxp, cyp);
  g.lineTo(cxp, cyp + 9);
  g.lineTo(cxp + 2.6, cyp + 6.6);
  g.lineTo(cxp + 6.4, cyp + 7);
  g.closePath();
  g.fillStyle = INK;
  g.fill();
  g.strokeStyle = "#fff";
  g.lineWidth = 0.8;
  g.stroke();
  const top = Math.min(tl[1], tr[1]);
  pill(g, (tl[0] + tr[0]) / 2, Math.max(top - 6, 50), COMMAND_APPS[focus], true, w);
}

const RELAY = ["Research", "Draft", "Design", "Review"];

// 02: four agents at desks pushed together, two facing two, passing the work round the pod
// until it ships.
function team(g, w, h, t) {
  const u = Math.min(w / 4.1, (h - 40) / 2.8);
  const ox = w / 2;
  const oy = 1.72 * u + 32;
  g.save();
  g.translate(ox, oy);
  const heads = drawAgentPod(g, u, (t / 1600) % 1).map(([x, y]) => [x + ox, y + oy]);
  g.restore();
  const leg = 1150;
  const cycle = leg * RELAY.length + 1500;
  const p = t % cycle;
  const done = p >= leg * RELAY.length;
  const at = Math.min(RELAY.length - 1, Math.floor(p / leg));
  const q = done ? 1 : ease(clamp((p % leg) / (leg * 0.55)));
  // The loop round the pod
  g.setLineDash([2, 5]);
  g.beginPath();
  heads.forEach(([x, y], k) => (k ? g.lineTo(x, y - 16) : g.moveTo(x, y - 16)));
  g.closePath();
  g.strokeStyle = "rgba(25,25,25,0.2)";
  g.lineWidth = 1;
  g.stroke();
  g.setLineDash([]);
  if (done) {
    const k = (p - leg * RELAY.length) / 1500;
    heads.forEach(([x, y], n) => {
      const pulse = clamp(k * 1.6 - n * 0.08);
      g.beginPath();
      g.arc(x, y, 8 + pulse * 16, 0, Math.PI * 2);
      g.strokeStyle = `rgba(25,25,25,${0.45 * (1 - pulse)})`;
      g.stroke();
    });
    const top = Math.min(...heads.map(([, y]) => y));
    pill(g, ox, Math.max(top - 26, 22), "Shipped ✓", true, w);
    return;
  }
  const from = heads[(at + heads.length - 1) % heads.length];
  const to = heads[at];
  const x0 = at === 0 ? to[0] : from[0];
  const y0 = at === 0 ? to[1] - 44 : from[1] - 16;
  const x1 = to[0];
  const y1 = to[1] - 16;
  const mx = (x0 + x1) / 2;
  const my = Math.min(y0, y1) - 18;
  const bx = (1 - q) * (1 - q) * x0 + 2 * (1 - q) * q * mx + q * q * x1;
  const by = (1 - q) * (1 - q) * y0 + 2 * (1 - q) * q * my + q * q * y1;
  roundRect(g, bx - 8, by - 6, 16, 12, 2);
  g.fillStyle = INK;
  g.fill();
  g.fillStyle = "#fff";
  g.fillRect(bx - 5, by - 2.5, 10, 1.4);
  g.fillRect(bx - 5, by + 1, 6, 1.4);
  if (q >= 1) {
    const pulse = (t % 900) / 900;
    g.beginPath();
    g.arc(to[0], to[1], 9 + pulse * 14, 0, Math.PI * 2);
    g.strokeStyle = `rgba(25,25,25,${0.45 * (1 - pulse)})`;
    g.lineWidth = 1;
    g.stroke();
    pill(g, to[0], to[1] - 28, RELAY[at], false, w);
  }
}

// The use cases on /api, each with its own worker topology and approval guardrail.
const USES = [
  { group: "Run operations", name: "Internal operations", file: "internal-operations.js", mode: "team", work: "Close the week's books", guard: "payments", roles: [["operations", 5], ["finance", 4], ["exceptions", 2]] },
  { group: "Build agent products", name: "Personal-assistant app", file: "assistant-app.js", mode: "persistent", work: "Plan tomorrow", guard: "messages", roles: [["assistant", 1], ["accounts", 3]] },
  { group: "Build AI-native companies", name: "Recruitment firm", file: "recruitment-firm.js", mode: "team", work: "Fill 5 roles", guard: "messages", roles: [["sourcing", 5], ["research", 4], ["coordination", 3]] },
  { group: "Run operations", name: "Customer operations", file: "customer-operations.js", mode: "team", work: "Clear the support queue", guard: "submissions", roles: [["support", 6], ["fulfilment", 4], ["policy checks", 2]] },
  { group: "Build agent products", name: "Vertical SaaS", file: "vertical-saas.js", mode: "team", work: "Submit prior auths", guard: "submissions", roles: [["workflow", 5], ["reviewer", 2]] },
  { group: "Build AI-native companies", name: "Autonomous marketplace", file: "marketplace.js", mode: "team", work: "Grow liquidity", guard: "payments", roles: [["supply", 4], ["demand", 4], ["ops", 3]] },
  { group: "Run operations", name: "BPO and shared services", file: "bpo-operations.js", mode: "team", work: "Clear client queues", guard: "submissions", roles: [["account teams", 8], ["service rules", 2], ["exceptions", 2]] },
  { group: "Build agent products", name: "General-purpose agent", file: "general-agent.js", mode: "single", work: "Prep the launch", guard: "messages", roles: [["multi-device worker", 1]] },
  { group: "Build AI-native companies", name: "VC firm", file: "vc-firm.js", mode: "team", work: "Diligence 10 startups", guard: "trades", roles: [["sourcing", 4], ["diligence", 4], ["portfolio", 3]] },
];
const LIFECYCLE = ["running", "needs_approval", "verifying", "completed ✓"];

// Your entire company, one department at a time.
const DEPTS = [
  { label: "support", role: "Support team", work: "Resolve every ticket", guard: "messages", count: 8 },
  { label: "sales", role: "Sales team", work: "Grow the pipeline", guard: "messages", count: 7 },
  { label: "finance", role: "Finance team", work: "Close the books", guard: "payments", count: 5 },
  { label: "research", role: "Research team", work: "Map the market", guard: "submissions", count: 6 },
];
const STEP = 3600;
const USE = 6400;
const COMPANY_ONLINE_AT = STEP * (DEPTS.length - 1) + STEP * 0.86;

const runCode = (worker, work, guard) => [
  ["await workers.run({", 0.55],
  [`  worker: { ${worker} },`, 0.9],
  [`  work: "${work}",`, 0.9],
  [`  guardrails: ["${guard}"],`, 0.9],
  ["});", 0.55],
];

// Where the story is: first your entire company rises floor by floor on the Workers API and its
// ops layer; then three use cases from /api each build their own stack; then it starts again.
function shotAt(t) {
  const act = DEPTS.length * STEP;
  const program = act + 3 * USE;
  const p = t % program;
  if (p < act) {
    const k = Math.floor(p / STEP);
    const local = (p % STEP) / STEP;
    const dept = DEPTS[k];
    const last = k === DEPTS.length - 1;
    return {
      local,
      group: "Build your entire company",
      file: "company.js",
      code: runCode(`role: "${dept.role}"`, dept.work, dept.guard),
      typed: clamp((local - 0.03) / 0.4),
      floors: DEPTS.map((d, f) => ({
        label: d.label,
        count: d.count,
        drop: f < k ? 1 : f === k ? ease(clamp((local - 0.47) / 0.1)) : 0,
        grow: f < k ? 1 : f === k ? clamp((local - 0.52) / 0.18) : 0,
      })),
      exit: last && local > 0.95 ? ease((local - 0.95) / 0.05) : 0,
      status: local < 0.47 ? null : last && local > 0.72 ? ["Company online", true] : local < 0.74 ? ["running", false] : ["completed ✓", false],
      approval: false,
    };
  }
  const k = Math.floor((p - act) / USE);
  const local = ((p - act) % USE) / USE;
  const use = USES[(Math.floor(t / program) * 3 + k) % USES.length];
  const count = use.roles.length;
  const stage = local < 0.42 ? -1 : local < 0.7 ? 0 : local < 0.8 ? 1 : local < 0.88 ? 2 : 3;
  return {
    local,
    group: use.group,
    file: use.file,
    code: runCode(`mode: "${use.mode}"`, use.work, use.guard),
    typed: clamp((local - 0.04) / 0.36),
    floors: use.roles.map(([role, n], f) => {
      const start = 0.42 + (f / count) * 0.2;
      return { label: role, count: n, drop: ease(clamp((local - start) / 0.07)), grow: clamp((local - start - 0.04) / 0.12) };
    }),
    exit: local > 0.965 ? ease((local - 0.965) / 0.035) : 0,
    status: stage < 0 ? null : stage < 3 ? [LIFECYCLE[stage], false] : [use.name, true],
    approval: stage === 1,
  };
}

// 03: a few lines of code, and a company rises on the Workers API and its ops layer.
function company(g, w, h, t, sprites) {
  // In narrow cards (phones, tablet columns) the code window sits above the building, full width.
  const stacked = w < 360;
  const shot = shotAt(t);
  const panelW = stacked ? w : Math.min(w * 0.47, 210);
  // Code window
  const top = stacked ? 22 : h * 0.13;
  const panelH = stacked ? 112 : Math.min(h * 0.72, 196);
  roundRect(g, 0, top, panelW, panelH, 8);
  g.fillStyle = "#fff";
  g.fill();
  g.strokeStyle = "rgba(25,25,25,0.18)";
  g.lineWidth = 1;
  g.stroke();
  g.fillStyle = "rgba(25,25,25,0.06)";
  g.fillRect(1, top + 1, panelW - 2, 22);
  [0, 1, 2].forEach((k) => {
    g.beginPath();
    g.arc(12 + k * 9, top + 12, 2.6, 0, Math.PI * 2);
    g.fillStyle = "rgba(25,25,25,0.28)";
    g.fill();
  });
  g.font = "500 10px Manrope, Arial, sans-serif";
  g.fillStyle = "rgba(25,25,25,0.6)";
  g.textBaseline = "middle";
  g.fillText(shot.file, 42, top + 12.5);
  const { code } = shot;
  const total = code.reduce((n, [line]) => n + line.length, 0);
  const typed = Math.floor(shot.typed * total);
  // Fit the longest line inside the window
  g.font = "500 10px ui-monospace, SFMono-Regular, Menlo, monospace";
  const widest = Math.max(...code.map(([line]) => g.measureText(line).width));
  const size = Math.min(10, (10 * (panelW - 22)) / widest);
  g.font = `500 ${size}px ui-monospace, SFMono-Regular, Menlo, monospace`;
  let left = typed;
  code.forEach(([line, alpha], k) => {
    const shown = line.slice(0, Math.max(0, left));
    left -= line.length;
    const y = top + (stacked ? 38 : 42) + k * (stacked ? 13.5 : (panelH - 56) / 5);
    g.fillStyle = `rgba(25,25,25,${alpha})`;
    g.fillText(shown, 12, y);
    if (left < 0 && left + line.length >= 0 && Math.floor(t / 400) % 2) {
      g.fillStyle = INK;
      g.fillRect(13 + g.measureText(shown).width, y - 6, 5, 12);
    }
  });
  // The stack: Workers API, the ops layer, then one floor per worker role
  const tw = stacked ? Math.min(w * 0.11, 38) : Math.min((w - panelW - 96) / 2, 46);
  const bx = stacked ? w / 2 - 24 : panelW + 14 + tw;
  const base = h - tw * 0.5 - 10;
  const ceiling = stacked ? top + panelH + 28 + tw * 0.5 : tw * 0.5 + 34;
  const floorH = Math.min(h * 0.16, 40, (base - ceiling) / (shot.floors.length + 1));
  g.font = "600 10px Manrope, Arial, sans-serif";
  g.fillStyle = "rgba(25,25,25,0.6)";
  g.textBaseline = "middle";
  g.fillText(shot.group, 2, top - 11);
  const slab = (y, label, alpha, workers, grow, dark) => {
    g.globalAlpha = alpha;
    const pts = [
      [bx, y - tw * 0.5],
      [bx + tw, y],
      [bx, y + tw * 0.5],
      [bx - tw, y],
    ];
    const depth = 6;
    const face = (a, b, fill) => {
      g.beginPath();
      g.moveTo(a[0], a[1]);
      g.lineTo(b[0], b[1]);
      g.lineTo(b[0], b[1] + depth);
      g.lineTo(a[0], a[1] + depth);
      g.closePath();
      g.fillStyle = fill;
      g.fill();
      g.strokeStyle = INK;
      g.lineWidth = 1;
      g.stroke();
    };
    face(pts[3], pts[2], dark ? "#262626" : "#ececec");
    face(pts[2], pts[1], dark ? "#111" : "#dcdcdc");
    g.beginPath();
    pts.forEach(([x, yy], k) => (k ? g.lineTo(x, yy) : g.moveTo(x, yy)));
    g.closePath();
    g.fillStyle = dark ? "#333" : "#fff";
    g.fill();
    g.stroke();
    if (dark) {
      // Guardrails, budgets and traces: a signal running round the ops layer
      const q = (t / 1600) % 1;
      const side = Math.floor(q * 4);
      const f = (q * 4) % 1;
      const a = pts[side];
      const b = pts[(side + 1) % 4];
      g.beginPath();
      g.arc(a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f, 2.2, 0, Math.PI * 2);
      g.fillStyle = "#fff";
      g.fill();
      [-0.45, 0, 0.45].forEach((k, n) => {
        const x = bx + k * tw;
        const yy = y + Math.abs(k) * tw * 0.02;
        g.fillStyle = "rgba(255,255,255,0.8)";
        if (n === 0) {
          g.beginPath();
          g.moveTo(x - 3, yy);
          g.lineTo(x - 1, yy + 2);
          g.lineTo(x + 3, yy - 2);
          g.strokeStyle = "#fff";
          g.lineWidth = 1.3;
          g.stroke();
        } else if (n === 1) g.fillRect(x - 5, yy - 1, 10 * ((t / 2000) % 1), 2);
        else [0, 1, 2].forEach((d) => g.fillRect(x - 4 + d * 3, yy - 1, 1.6, 1.6));
      });
    }
    const u = tw * 0.1;
    const shown = Math.floor(workers * grow);
    for (let n = 0; n < shown; n += 1) {
      const gi = workers === 1 ? 0 : (n % 4) / 3 - 0.5;
      const gj = workers === 1 ? 0 : Math.floor(n / 4) / 2 - 0.5;
      const x = bx + (gi - gj) * tw * 0.7;
      const yy = y + (gi + gj) * tw * 0.35;
      g.drawImage(sprites[(n + Math.floor(t / 160)) % sprites.length], x + BOX.left * u, yy + BOX.top * u, BOX.width * u, BOX.height * u);
    }
    g.globalAlpha = 1;
    if (label) {
      g.font = `600 ${dark ? 10 : 9.5}px Manrope, Arial, sans-serif`;
      g.fillStyle = dark ? INK : "rgba(25,25,25,0.7)";
      g.textBaseline = "middle";
      g.fillText(label, bx + tw + 7, y + 3);
    }
  };
  const { exit } = shot;
  // The building's frame
  const built = shot.floors.reduce((n, f) => n + f.drop, 0);
  if (built > 0.05) {
    g.globalAlpha = 1 - exit;
    g.beginPath();
    for (const [dx, dy] of [[-tw, 0], [0, tw * 0.5], [tw, 0]]) {
      g.moveTo(bx + dx, base - floorH + dy);
      g.lineTo(bx + dx, base - floorH - Math.ceil(built) * floorH + dy);
    }
    g.strokeStyle = "rgba(25,25,25,0.3)";
    g.lineWidth = 1;
    g.stroke();
    g.globalAlpha = 1;
  }
  slab(base, "Workers API", 1, 0, 0, false);
  slab(base - floorH, "Ops layer", 1, 0, 0, true);
  shot.floors.forEach(({ label, count, drop, grow }, f) => {
    if (drop <= 0) return;
    const y = base - (f + 2) * floorH - (1 - drop) * 26 - exit * 30;
    slab(y, label, drop * (1 - exit), count, grow, false);
  });
  // The run's lifecycle, then the business on the roof
  const roof = Math.max(base - (Math.ceil(built) + 2) * floorH - 16, stacked ? top + panelH + 26 : 24);
  if (shot.status && exit < 0.5) {
    pill(g, bx, roof, shot.status[0], shot.status[1], w);
    if (shot.approval) {
      const pulse = (t % 700) / 700;
      g.beginPath();
      g.ellipse(bx, base - floorH, tw * (1 + pulse * 0.4), tw * 0.5 * (1 + pulse * 0.4), 0, 0, Math.PI * 2);
      g.strokeStyle = `rgba(25,25,25,${0.5 * (1 - pulse)})`;
      g.stroke();
    }
  }
}

// The three products as living scenes: one worker, a team, a company.
export default function ProductScene({ index }) {
  const host = useRef(null);
  const canvasRef = useRef(null);
  useEffect(() => {
    const box = host.current;
    const canvas = canvasRef.current;
    if (!box || !canvas || !window.matchMedia) return undefined;
    const ctx = canvas.getContext?.("2d");
    if (!ctx) return undefined;
    const animate = canAnimate();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let sprites = [];
    let raf = 0;
    let visible = false;
    let clock = 0;
    let last = 0;
    let fast = false;
    const resize = () => {
      const rect = box.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (index === 2) sprites = humanoidSprites(8, dpr, { screen: false });
    };
    const draw = (t) => {
      ctx.clearRect(0, 0, w, h);
      if (index === 0) personal(ctx, w, h, t);
      else if (index === 1) team(ctx, w, h, t);
      else company(ctx, w, h, t, sprites);
    };
    const frame = (now) => {
      raf = visible ? requestAnimationFrame(frame) : 0;
      clock += Math.min(50, now - (last || now)) * (fast ? 1.8 : 1);
      last = now;
      draw(clock);
    };
    resize();
    draw(index === 2 ? COMPANY_ONLINE_AT : 900);
    const card = box.closest("a");
    const onEnter = () => (fast = true);
    const onLeave = () => (fast = false);
    card?.addEventListener("pointerenter", onEnter);
    card?.addEventListener("pointerleave", onLeave);
    const observer =
      animate && window.IntersectionObserver
        ? new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting;
            last = 0;
            if (visible && !raf) raf = requestAnimationFrame(frame);
          })
        : null;
    observer?.observe(box);
    const resizer = window.ResizeObserver
      ? new ResizeObserver(() => {
          if (Math.abs(box.getBoundingClientRect().width - w) > 2) {
            resize();
            draw(clock || 900);
          }
        })
      : null;
    resizer?.observe(box);
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      resizer?.disconnect();
      card?.removeEventListener("pointerenter", onEnter);
      card?.removeEventListener("pointerleave", onLeave);
    };
  }, [index]);
  return (
    <div ref={host} className="bm-product-scene" aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
}
